import React, { memo } from 'react';
// Fix: Use named imports for Handle, Position, and NodeProps from reactflow.
import { Handle, Position, NodeProps } from 'reactflow';
import * as Icons from 'lucide-react';
import { LucideIcon, Settings2, Code, Mail, Database, Bot, GitFork, List, Users, Clock, Zap, Workflow, Circle } from 'lucide-react';
import { WorkflowNodeData, NodeCategory } from '../../types';
import { useEditorConfig } from '../../contexts/EditorContext';

// Safe icon lookup table (typed via a single cast at module scope).
const ICON_LIBRARY = Icons as unknown as Record<string, LucideIcon | undefined>;

// Static tab -> icon component mapping. Kept module-level so the render path
// is a single dictionary lookup instead of a long switch, cutting branch count.
const TAB_ICON_MAP: Record<string, LucideIcon> = {
  properties: Settings2,
  form: List,
  assignments: Users,
  script: Code,
  http: Icons.Globe,
  email: Mail,
  llm: Bot,
  routing: GitFork,
  conditions: GitFork,
  timer: Clock,
  message: Mail,
  signal: Zap,
  database: Database,
  call: Workflow,
  fileimport: Icons.Upload,
  extraction: Icons.Filter,
};

const resolveTabIcon = (tabId: string): LucideIcon => {
  const mapped = TAB_ICON_MAP[tabId];
  if (mapped) return mapped;
  // Dynamic fallback: try exact key, then PascalCase variant of the tab id.
  const dynamic =
    ICON_LIBRARY[tabId] ||
    ICON_LIBRARY[tabId.charAt(0).toUpperCase() + tabId.slice(1)];
  return dynamic || Settings2;
};

const CustomNode = ({ id, data, type, selected, isConnectable }: NodeProps<WorkflowNodeData>) => {
  const { nodeTypes, propertyRegistry, actions } = useEditorConfig();
  const config = nodeTypes[type] || nodeTypes['FormStartEvent'];

  const IconComponent = ICON_LIBRARY[config.icon] || Icons.HelpCircle;

  // Tabs for shortcuts
  const nodeConfig = propertyRegistry[type] || { tabs: [] };
  const tabs = nodeConfig.tabs || [];

  const getTabIcon = (tabId: string) => {
    const Icon = resolveTabIcon(tabId);
    return <Icon size={10} />;
  };

  const getProfile = () => {
    if (data.isConnectableTarget) {
      return { 
          border: 'border-teal-400 dark:border-teal-500', 
          bg: 'bg-teal-50 dark:bg-teal-900/30', 
          accent: 'text-teal-600 dark:text-teal-400', 
          ring: 'ring-teal-400/20', 
          showStatus: false 
      };
    }

    if (data.isBoundaryCandidate) {
        return { 
          border: 'border-teal-500 dark:border-teal-400 scale-105', 
          bg: 'bg-teal-50/50 dark:bg-teal-900/20', 
          accent: 'text-teal-600 dark:text-teal-400', 
          ring: 'ring-teal-500/40 shadow-xl shadow-teal-500/10', 
          showStatus: false 
        };
    }

    if (data.isPotentialBoundaryTarget) {
        return { 
          border: 'border-teal-300 dark:border-teal-700 border-dashed', 
          bg: 'bg-teal-50/10 dark:bg-teal-900/10', 
          accent: 'text-teal-400 dark:text-teal-500', 
          ring: 'ring-teal-300/10', 
          showStatus: false 
        };
    }
    
    switch (data.status) {
      case 'error':
        return { 
          border: 'border-red-400 dark:border-red-500', 
          bg: 'bg-red-50 dark:bg-red-900/20', 
          accent: 'text-red-600 dark:text-red-400', 
          statusLabel: 'Error', 
          statusIconColor: 'fill-red-500 dark:fill-red-400',
          badgeBg: 'bg-red-600 dark:bg-red-500',
          pillBg: 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50',
          pulse: true,
          showStatus: true
        };
      case 'waiting':
        return { 
          border: 'border-amber-300 dark:border-amber-600', 
          bg: 'bg-amber-50 dark:bg-amber-900/20', 
          accent: 'text-amber-600 dark:text-amber-400', 
          statusLabel: 'Suspended', 
          statusIconColor: 'fill-amber-500 dark:fill-amber-400',
          badgeBg: 'bg-amber-600 dark:bg-amber-500',
          pillBg: 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50',
          showStatus: true
        };
      case 'completed':
        return { 
          border: 'border-teal-500 dark:border-teal-500', 
          bg: 'bg-white dark:bg-slate-900', 
          accent: 'text-teal-600 dark:text-teal-400', 
          statusLabel: 'Completed', 
          statusIconColor: 'fill-teal-500 dark:fill-teal-400',
          badgeBg: 'bg-teal-600 dark:bg-teal-500',
          pillBg: 'bg-teal-50 text-teal-700 hover:bg-teal-100 dark:bg-teal-900/30 dark:text-teal-300 dark:hover:bg-teal-900/50',
          showStatus: true
        };
      case 'active':
        return { 
          border: 'border-teal-500 dark:border-teal-500', 
          bg: 'bg-white dark:bg-slate-900', 
          accent: 'text-teal-600 dark:text-teal-400', 
          statusLabel: 'Active', 
          statusIconColor: 'fill-emerald-500 dark:fill-emerald-400',
          badgeBg: 'bg-teal-600 dark:bg-teal-500',
          ring: 'ring-teal-500/20',
          pillBg: 'bg-teal-50 text-teal-700 hover:bg-teal-100 dark:bg-teal-900/30 dark:text-teal-300 dark:hover:bg-teal-900/50',
          showStatus: true
        };
      case 'idle':
      default:
        return { 
          border: 'border-slate-200 dark:border-slate-700', 
          bg: 'bg-slate-50 dark:bg-slate-900', 
          accent: 'text-slate-500 dark:text-slate-400', 
          statusLabel: 'Inactive', 
          statusIconColor: 'fill-slate-400 dark:fill-slate-500',
          badgeBg: 'bg-slate-600 dark:bg-slate-700',
          pillBg: 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700',
          showStatus: false
        };
    }
  };

  const profile = getProfile();
  const isActiveSelection = selected && actions?.activeNodeId === id;
  const isBoundaryEvent = config.category === NodeCategory.Boundary;
  const isStartEvent = config.category === NodeCategory.Start;
  const isEndEvent = config.category === NodeCategory.End;

  const handleStyle = "!w-3 !h-3 !bg-slate-400 dark:!bg-slate-500 !border-2 !border-white dark:!border-slate-800 hover:!bg-teal-500 transition-colors z-50";

  if (isBoundaryEvent) {
    const isInterrupting = data.cancelActivity !== false;
    return (
      <div className="relative group">
        <div 
          className={`
            relative w-14 h-14 rounded-full border-2 bg-white dark:bg-slate-900 transition-all duration-300
            ${profile.border} ${isActiveSelection ? 'ring-4 ring-teal-500/60 scale-110 shadow-xl' : selected ? 'ring-4 ring-teal-500/30 shadow-lg' : 'shadow-sm group-hover:shadow-md'}
            ${data.monitorSelected ? 'ring-4 ring-teal-500/60 shadow-lg' : ''}
            ${isInterrupting ? 'border-solid' : 'border-dashed'}
          `}
        >
          {typeof data.monitorCount === 'number' && data.monitorCount > 0 && (
            <div className="absolute -top-2 -right-2 z-40 min-w-[20px] h-5 px-1 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center shadow-md ring-2 ring-white dark:ring-slate-900">
              {data.monitorCount}
            </div>
          )}
          <Handle type="target" position={Position.Top} className={handleStyle} isConnectable={isConnectable} />
          <div className="flex items-center justify-center h-full" style={{ color: config.color }}>
            <IconComponent size={24} strokeWidth={2.5} />
          </div>
          <Handle type="source" position={Position.Right} className={handleStyle} isConnectable={isConnectable} />
        </div>
        
        {/* Label under the boundary event circle */}
        <div className="absolute top-[100%] left-1/2 -translate-x-1/2 pt-1 pointer-events-none w-max max-w-[120px]">
            <p className="text-[10px] font-bold text-slate-600 dark:text-slate-300 text-center leading-tight drop-shadow-sm bg-white/80 dark:bg-slate-900/80 px-1 rounded backdrop-blur-sm">
                {data.label || config.label}
            </p>
        </div>
      </div>
    );
  }

  const handlePillClick = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    if (actions?.openModal) {
        actions.openModal(id, tabId);
    }
  };

  return (
    <div
      className={`
        relative px-4 py-3 rounded-xl border-2 transition-all duration-300 min-w-[220px] max-w-[280px]
        ${profile.bg} ${profile.border}
        ${isActiveSelection ? 'ring-4 ring-teal-500/60 scale-105 shadow-xl' : selected ? 'ring-4 ring-teal-500/30 shadow-xl' : 'shadow-lg'}
        ${data.monitorSelected ? 'ring-4 ring-teal-500/60 shadow-xl' : ''}
        ${data.actorType ? 'pr-5' : ''}
      `}
    >
      {typeof data.monitorCount === 'number' && data.monitorCount > 0 && (
        <div className="absolute -top-3 -right-3 z-40 min-w-[24px] h-6 px-1.5 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white dark:ring-slate-900">
          {data.monitorCount}
        </div>
      )}
      {data.actorType && (
        <div
          className="absolute top-0 right-0 bottom-0 w-4 rounded-r-[10px] bg-slate-100/70 dark:bg-slate-800/60 border-l border-slate-200/60 dark:border-slate-700/60 flex items-center justify-center overflow-hidden pointer-events-none"
          title={`Actor Type: ${data.actorType}`}
        >
          <span
            className="text-[9px] font-medium tracking-wide text-slate-500 dark:text-slate-400 whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {data.actorType}
          </span>
        </div>
      )}
      <div className={`
        absolute -top-3 left-4 px-2 py-0.5 rounded-full text-[9px] font-bold text-white uppercase tracking-widest
        ${profile.badgeBg} ${profile.pulse ? 'animate-pulse' : ''}
      `}>
        {data.status === 'error' ? 'Cycle Error' : (data.categoryLabel || config.category)}
      </div>

      {data.hasBreakpoint && (
        <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full border-2 border-white z-30 shadow-md flex items-center justify-center animate-pulse">
            <div className="w-1.5 h-1.5 bg-white rounded-full" />
        </div>
      )}

      {!isStartEvent && (() => {
        const showAll = data.isConnectableTarget === true;
        const leftUsed = data.targetHandleUsage?.left ?? false;
        const topUsed = data.targetHandleUsage?.top ?? false;
        // Fallback: show Left when nothing is connected yet (matches pre-feature behaviour).
        const showLeft = showAll || leftUsed || !topUsed;
        const showTop = showAll || topUsed;
        const hiddenStyle = '!opacity-0 !pointer-events-none';
        return (
          <>
            <Handle type="target" position={Position.Left} id="t-left" className={`${handleStyle} ${showLeft ? '' : hiddenStyle}`} isConnectable={isConnectable} />
            <Handle type="target" position={Position.Top} id="t-top" className={`${handleStyle} ${showTop ? '' : hiddenStyle}`} isConnectable={isConnectable} />
          </>
        );
      })()}

      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-xl bg-slate-100 dark:bg-slate-800 shrink-0 ${profile.accent}`}>
          <IconComponent size={20} strokeWidth={2.5} />
        </div>
        <div className="flex-1 overflow-hidden">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate leading-tight">
            {data.label || config.label}
          </h3>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate font-mono" title={id}>
            {id}
          </p>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-slate-200/50 dark:border-slate-700/50 flex items-center justify-between min-h-[24px]">
        <div className="flex items-center gap-1 overflow-hidden no-scrollbar">
            {/* Tab pills are edit shortcuts; they stopPropagation, which would swallow
                the node click that drives monitor selection. Hide them in monitor mode. */}
            {!data.monitorMode && tabs.map(tab => (
                <button
                    key={tab.id}
                    onClick={(e) => handlePillClick(e, tab.id)}
                    title={tab.label}
                    className={`flex items-center justify-center w-5 h-5 rounded-md transition-all shrink-0 ${profile.pillBg}`}
                >
                    {getTabIcon(tab.id)}
                </button>
            ))}
        </div>
        
        {profile.showStatus && (
          <div className={`flex items-center gap-1 text-[9px] font-bold uppercase ${profile.accent} animate-in fade-in duration-300 ml-2`}>
             <Circle size={8} className={`${profile.statusIconColor} transition-colors`} /> {profile.statusLabel}
          </div>
        )}
      </div>

      {!isEndEvent && (() => {
        const isGateway = type === 'XorGateway' || type === 'InclusiveGateway' || type === 'ParallelGateway';
        const isSplitting = isGateway && !data.isMerging;
        const outputs: string[] = data.outputs || [];

        if (isSplitting && outputs.length > 0) {
          return outputs.map((_label: string, idx: number) => {
            const spacing = 100 / (outputs.length + 1);
            const topPercent = spacing * (idx + 1);
            return (
              <Handle
                key={`out-${idx}`}
                type="source"
                position={Position.Right}
                id={String(idx)}
                className={handleStyle}
                style={{ top: `${topPercent}%`, right: '-6px' }}
                isConnectable={isConnectable}
              />
            );
          });
        }

        return (
          <Handle type="source" position={Position.Right} className={handleStyle} style={{ right: '-6px' }} isConnectable={isConnectable} />
        );
      })()}

      {(config.category === NodeCategory.Task || config.category === NodeCategory.Activity) && (
        <Handle
            type="source"
            position={Position.Bottom}
            id="boundary"
            className="!absolute !w-[32px] !h-[6px] !rounded-[2px] !bg-slate-300 dark:!bg-slate-600 !border-2 !border-white dark:!border-slate-800 hover:!bg-teal-500 transition-colors z-50 opacity-0 hover:opacity-100"
            title="Attach Boundary Event"
            isConnectable={isConnectable}
        />
      )}
    </div>
  );
};

export default memo(CustomNode);
